/**
 * <TutorGenerationConfigurator> — « Plus d'options » de la génération au live du masteur, au centre (organism).
 *
 * Wajih, live du 04/11 : la colonne du champion était trop chargée. Le champ du bas en garde la version courte ;
 * « Plus d'options » ouvre ici, par-dessus le centre comme la relecture (`<TutorReviewStage>`), le configurateur de
 * l'ancienne fenêtre (`GenerateActivityModal.js`), sans rien en perdre :
 *  - le formulaire entier (`<ActivityGenerationForm>`, la voix du tuteur) : la classe et la matière, le chapitre et sa
 *    recherche (le prochain contrôle en tête, une recherche sans issue qui bascule vers les instructions), les
 *    compétences, le type, la durée, les instructions, « Personnaliser » ;
 *  - la pièce d'où part la génération (`source`, celle du champ : une similaire, une image, une photo du chat), et
 *    pour une image ce que le champion en a lu (`analysis` : le chapitre qu'il a choisi, ou une autre classe à accepter) ;
 *  - joindre une photo ou un PDF d'exercice (`onAttach`) ;
 *  - « Générer » : les propositions arrivent dans la relecture, au même endroit.
 *
 * Deux modes (`mode`), en onglets sous le titre, quand une activité peut servir de source :
 *  - **À partir d'une activité** : la source (l'activité ouverte, ou la dernière rendue), puis la difficulté, le type,
 *    la durée et une consigne ; la classe, la matière et le chapitre viennent de la source ;
 *  - **À partir de zéro**, au plus près de l'ancienne fenêtre : la classe et la matière pré-remplies, le chapitre
 *    cherché en tapant (le programme de l'élève en tête), le type, la durée, les instructions, partir d'une photo.
 *
 * Le même brouillon que le champ : ce qui se règle ici se lit dans ses puces, et inversement. Échap, la croix ou le
 * voile referment sans rien perdre. Contrôlé : le brouillon, ses catalogues et ce qui manque arrivent en props.
 */
import React, { useEffect, useRef } from "react"
import { Animated, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { ScrollView } from "../../atoms/ScrollView"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { usePresence } from "../../hooks/usePresence"
import { Tabs } from "../../molecules/Tabs"
import { ActivityAnalysisNotice, type ActivityAnalysisNoticeProps } from "../../molecules/ActivityAnalysisNotice"
import { Kbd, TutorDraftSourcePiece, type UITutorDraftSource } from "../../molecules/TutorDraftComposer"
import { shadowStyle } from "../../utils/shadow"
import { listenEscape } from "../../utils/listenEscape"
import { listenEnterKey } from "../../utils/shutterKey"
import { ActivityGenerationForm, type ActivityGenerationFormProps } from "../ActivityGenerationForm"

export type TutorGenerationConfiguratorProps = {
    open: boolean
    /** Pour qui : « Léa », « tout le groupe ». */
    studentName: string
    /** Le formulaire : le brouillon partagé avec le champ, ses catalogues, ce qui manque. */
    form: Omit<ActivityGenerationFormProps, "voice" | "testID">
    /** La pièce d'où part la génération. Absente : le formulaire seul. */
    source?: UITutorDraftSource
    /** Une image : ce que le champion en a lu, sous le chapitre (comme l'ancienne fenêtre). Absent : rien. */
    analysis?: Omit<ActivityAnalysisNoticeProps, "voice" | "testID">
    /** « Joindre une photo ou un PDF ». Absent : pas de bouton (une similaire n'en a pas besoin). */
    onAttach?: () => void
    onGenerate: () => void
    /** Une génération part déjà : « Générer » tourne. */
    generating?: boolean
    onClose: () => void
    /** Une présentation tourne : les élèves voient le centre, il le dit. */
    presenting?: boolean
    /** Les deux modes, quand une activité peut servir de source. Absent : le formulaire tel qu'il est, sans onglets. */
    mode?: { value: TutorGenerationMode; onChange: (next: TutorGenerationMode) => void }
    testID?: string
}

/** Partir d'une activité (la source donne la classe, la matière, le chapitre), ou de zéro (tout le formulaire). */
export type TutorGenerationMode = "activity" | "scratch"

const TEXTS = {
    eyebrow: (name: string) => `Nouvelle activité · pour ${name}`,
    title: "Plus d'options",
    close: "Fermer les options",
    escape: "Échap",
    attach: "Joindre une photo ou un PDF",
    generate: "Générer",
    presenting: "Tu présentes : tes élèves voient ces options.",
    modes: "D'où part l'activité",
    fromActivity: "À partir d'une activité",
    fromScratch: "À partir de zéro",
}

/** Le formulaire ne s'étale pas plus large : ses champs se lisent d'un coup d'œil. */
const FORM_MAX = 640

export function TutorGenerationConfigurator(props: TutorGenerationConfiguratorProps) {
    const {
        open,
        studentName,
        form,
        source,
        analysis,
        onAttach,
        onGenerate,
        generating = false,
        onClose,
        presenting = false,
        mode,
        testID,
    } = props
    const { progress, mounted } = usePresence(open)
    const keys = useRef({ onClose, onGenerate, generating })
    keys.current = { onClose, onGenerate, generating }
    useEffect(() => (open ? listenEscape(() => keys.current.onClose()) : undefined), [open])
    // « Entrée » au web génère, hors d'une saisie (les instructions) et d'un bouton atteint à la tabulation : après
    // un clic (un onglet, un champ), elle génère quand même.
    useEffect(
        () =>
            open
                ? listenEnterKey(() => {
                      if (keys.current.generating) return false
                      keys.current.onGenerate()
                      return true
                  })
                : undefined,
        [open]
    )

    if (!mounted) return null
    const rise = progress.interpolate({ inputRange: [0, 1], outputRange: [16, 0] })

    return (
        <View style={styles.root} testID={testID}>
            <Animated.View style={[styles.scrim, { opacity: progress }]}>
                <Pressable style={styles.fill} onPress={onClose} accessibilityLabel={TEXTS.close} />
            </Animated.View>
            <Animated.View style={[styles.stage, { opacity: progress, transform: [{ translateY: rise }] }]}>
                <Row align="start" justify="between" style={styles.head}>
                    <Stack gap="1" style={styles.grow}>
                        <Text size="text-xs" weight="bold" color="role-strong" uppercase>
                            {TEXTS.eyebrow(studentName)}
                        </Text>
                        <Text size="text-2xl" weight="bold" color="text-strong" accessibilityRole="header">
                            {TEXTS.title}
                        </Text>
                    </Stack>
                    <Row gap="2" align="center">
                        <Kbd label={TEXTS.escape} />
                        <IconButton
                            iconName="times"
                            appearance="transparent"
                            iconColor="text-strong"
                            size={40}
                            onPress={onClose}
                            accessibilityLabel={TEXTS.close}
                        />
                    </Row>
                </Row>
                {mode != null && (
                    <View style={styles.modes}>
                        <Tabs
                            size="sm"
                            fadeColor="surface-raised"
                            accessibilityLabel={TEXTS.modes}
                            items={[
                                { key: "activity", label: TEXTS.fromActivity },
                                { key: "scratch", label: TEXTS.fromScratch },
                            ]}
                            value={mode.value}
                            onChange={mode.onChange}
                        />
                    </View>
                )}
                {presenting && (
                    <View style={styles.banner}>
                        <Banner color="warning" tinted icon="chalkboard-teacher" message={TEXTS.presenting} />
                    </View>
                )}
                <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
                    <Stack gap="3" style={styles.form}>
                        {source != null && <TutorDraftSourcePiece source={source} />}
                        <ActivityGenerationForm
                            {...form}
                            voice="student"
                            compact
                            analysisSlot={
                                analysis != null ? <ActivityAnalysisNotice {...analysis} voice="student" /> : undefined
                            }
                        />
                    </Stack>
                </ScrollView>
                <Row align="center" justify="between" style={styles.foot}>
                    {onAttach != null ? (
                        <Button
                            label={TEXTS.attach}
                            iconName="paperclip"
                            variant="ghost"
                            size="sm"
                            onPress={onAttach}
                        />
                    ) : (
                        <View />
                    )}
                    <Row gap="2" align="center">
                        <Button label={TEXTS.generate} iconName="magic" onPress={onGenerate} loading={generating} />
                        <Kbd label="↵" />
                    </Row>
                </Row>
            </Animated.View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { position: "absolute" as const, top: 0, left: 0, right: 0, bottom: 0, zIndex: 5 },
    fill: { flex: 1 },
    grow: { flex: 1, minWidth: 0 },
    scrim: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.colors.scrim,
    },
    stage: {
        flex: 1,
        margin: theme.spacing["3"],
        borderRadius: theme.radius.xl,
        backgroundColor: theme.colors["surface-raised"],
        overflow: "hidden" as const,
        ...shadowStyle(theme, "floating"),
    },
    head: {
        gap: theme.spacing["3"],
        paddingHorizontal: theme.spacing["6"],
        paddingTop: theme.spacing["5"],
        paddingBottom: theme.spacing["3"],
    },
    banner: { paddingHorizontal: theme.spacing["6"], paddingBottom: theme.spacing["3"] },
    modes: { paddingHorizontal: theme.spacing["6"] },
    body: {
        flex: 1,
        minHeight: 0,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: theme.colors["border-subtle"],
    },
    bodyContent: { alignItems: "center" as const, padding: theme.spacing["5"] },
    form: { width: "100%" as const, maxWidth: FORM_MAX },
    foot: {
        flexWrap: "wrap" as const,
        gap: theme.spacing["3"],
        paddingHorizontal: theme.spacing["6"],
        paddingVertical: theme.spacing["4"],
    },
}))
